import { api } from '../lib/api.js';

/**
 * Save-game storage for the editor's Play view (`host.loadSaves` /
 * `host.writeSave` / `host.deleteSave`, see ./README.md). The backend keeps
 * one `<slot>.json` per slot in the project's `.prism/saves/` folder, so
 * `save_game` / `load_game` survive restarting Play and the editor.
 */
export function projectSaveHost(projectId: number) {
  const base = `/projects/${projectId}/saves`;
  return {
    loadSaves: async (): Promise<Record<string, string>> => {
      const result = await api.get<{ saves: Record<string, string> }>(base);
      return result.saves;
    },
    writeSave: (slot: string, json: string): Promise<unknown> =>
      api.put(`${base}/${encodeURIComponent(slot)}`, { data: json }),
    deleteSave: (slot: string): Promise<unknown> => api.del(`${base}/${encodeURIComponent(slot)}`),
  };
}

/** The saves the engine loads at start and writes as the game saves. */
export interface SaveHost {
  loadSaves(): Promise<Record<string, string>>;
  writeSave(slot: string, json: string): Promise<unknown>;
  deleteSave(slot: string): Promise<unknown>;
}

const ARCADE_DB = 'prism-arcade-saves';
const ARCADE_STORE = 'saves';

let arcadeDb: Promise<IDBDatabase> | null = null;
function openArcadeDb(): Promise<IDBDatabase> {
  arcadeDb ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(ARCADE_DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(ARCADE_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      arcadeDb = null;
      reject(req.error ?? new Error('IndexedDB unavailable'));
    };
  });
  return arcadeDb;
}

function idb<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openArcadeDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const req = run(db.transaction(ARCADE_STORE, mode).objectStore(ARCADE_STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
      }),
  );
}

/**
 * Save-game storage for games played on the website (the arcade player): this
 * browser's IndexedDB, one record per slot under the game's key, so a world
 * and its players survive closing the page. (Without it saves lived only as
 * long as the page: every visit started over.) IndexedDB rather than
 * localStorage: a survival world's save is many megabytes.
 */
export function arcadeSaveHost(gameKey: string): SaveHost {
  const prefix = `${gameKey}\u0000`;
  const range = () => IDBKeyRange.bound(prefix, `${prefix}\uffff`);
  return {
    loadSaves: async () => {
      try {
        const [keys, values] = await Promise.all([
          idb<IDBValidKey[]>('readonly', (s) => s.getAllKeys(range())),
          idb<unknown[]>('readonly', (s) => s.getAll(range())),
        ]);
        const out: Record<string, string> = {};
        keys.forEach((k, i) => {
          if (typeof k === 'string' && typeof values[i] === 'string') out[k.slice(prefix.length)] = values[i] as string;
        });
        return out;
      } catch {
        // Private windows / storage disabled: the game still runs, without saves.
        return {};
      }
    },
    writeSave: (slot, json) => idb('readwrite', (s) => s.put(json, prefix + slot)),
    deleteSave: (slot) => idb('readwrite', (s) => s.delete(prefix + slot)),
  };
}
